import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft,
  Trophy,
  Medal,
  Award,
  Crown,
  Star,
  Users,
  TrendingUp,
  SlidersHorizontal,
  RotateCcw
} from "lucide-react";
import { motion } from "framer-motion";

export default function Leaderboard() {
  const navigate = useNavigate();
  const { classId } = useParams();
  const { user, token } = useAuth();
  const [classLeaderboard, setClassLeaderboard] = useState(null);
  const [globalLeaderboard, setGlobalLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustStudent, setAdjustStudent] = useState(null);
  const [adjustData, setAdjustData] = useState({ bonus_points: 0, note: "" });
  const [adjusting, setAdjusting] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // 'class' or 'global'

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    fetchLeaderboards();
  }, [classId]);

  useEffect(() => {
    if (activeSubject !== null) fetchGlobalLeaderboard(activeSubject);
  }, [activeSubject]);

  const fetchLeaderboards = async () => {
    try {
      // Pass classId so backend can scope subjects for students on a class-specific page.
      const globalQuery = classId ? `?class_id=${encodeURIComponent(classId)}` : "";
      const promises = [
        axios.get(`${API}/leaderboard/global${globalQuery}`, { headers })
      ];
      if (classId) {
        promises.push(axios.get(`${API}/leaderboard/class/${classId}`, { headers }));
      }
      const results = await Promise.all(promises);
      const globalData = results[0].data;
      setGlobalLeaderboard(globalData);
      setSubjects(globalData.subjects || []);

      // For students: auto-select first subject if available
      if (user?.role === "student" && globalData.subjects?.length > 0 && activeSubject === null) {
        setActiveSubject(globalData.subjects[0]);
      }

      if (classId && results[1]) {
        setClassLeaderboard(results[1].data);
      }
    } catch (error) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalLeaderboard = async (subject) => {
    try {
      const params = new URLSearchParams();
      if (subject) params.set("subject", subject);
      if (classId) params.set("class_id", classId);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/leaderboard/global${qs}`, { headers });
      setGlobalLeaderboard(res.data);
    } catch (error) {
      toast.error("Failed to load leaderboard");
    }
  };

  const openAdjustDialog = (entry) => {
    setAdjustStudent(entry);
    setAdjustData({
      bonus_points: entry.bonus_points || 0,
      note: entry.adjustment_note || ""
    });
    setAdjustDialogOpen(true);
  };

  const handleAdjustRanking = async () => {
    setAdjusting(true);
    try {
      await axios.put(
        `${API}/leaderboard/class/${classId}/adjust`,
        { student_id: adjustStudent.student_id, ...adjustData },
        { headers }
      );
      toast.success(`Ranking adjusted for ${adjustStudent.student_name}`);
      setAdjustDialogOpen(false);
      fetchLeaderboards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust ranking");
    } finally {
      setAdjusting(false);
    }
  };

  const openResetDialog = (target) => {
    setResetTarget(target);
    setResetDialogOpen(true);
  };

  const handleResetLeaderboard = async () => {
    setResetting(true);
    try {
      if (resetTarget === "class") {
        await axios.post(`${API}/leaderboard/class/${classId}/reset`, {}, { headers });
        toast.success("Class leaderboard has been reset");
      } else {
        const params = activeSubject ? `?subject=${encodeURIComponent(activeSubject)}` : "";
        await axios.post(`${API}/leaderboard/global/reset${params}`, {}, { headers });
        toast.success(activeSubject ? `${activeSubject} leaderboard has been reset` : "Global leaderboard has been reset");
      }
      setResetDialogOpen(false);
      fetchLeaderboards();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset leaderboard");
    } finally {
      setResetting(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) return "bg-primary/10 border-primary/30";
    if (rank === 1) return "bg-yellow-50 border-yellow-200";
    if (rank === 2) return "bg-gray-50 border-gray-200";
    if (rank === 3) return "bg-amber-50 border-amber-200";
    return "bg-card border-border/50";
  };

  const LeaderboardList = ({ data, showBadges = false, showAdjust = false }) => (
    <div className="space-y-2">
      {data.map((entry, index) => (
        <motion.div
          key={entry.student_id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          className={`p-3 md:p-4 rounded-xl border transition-all ${getRankBg(entry.rank, entry.is_current_user)}`}
          data-testid={`leaderboard-entry-${entry.student_id}`}
        >
          <div className="flex items-center gap-2 md:gap-4">
            {/* Rank */}
            <div className="w-8 md:w-10 flex justify-center shrink-0">
              {getRankIcon(entry.rank)}
            </div>
            
            {/* Avatar & Name */}
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-primary font-semibold text-sm md:text-base">
                {entry.student_name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 md:gap-2 flex-wrap">
                <p className="font-semibold text-foreground text-sm md:text-base truncate">
                  {entry.student_name}
                  {entry.is_current_user && (
                    <Badge className="ml-1 md:ml-2 bg-primary/10 text-primary hover:bg-primary/10 text-[10px] md:text-xs">You</Badge>
                  )}
                </p>
                {entry.bonus_points !== 0 && entry.bonus_points !== undefined && (
                  <Badge variant="outline" className={`text-[10px] md:text-xs ${entry.bonus_points > 0 ? 'text-green-600 border-green-300' : 'text-red-600 border-red-300'}`}>
                    {entry.bonus_points > 0 ? '+' : ''}{entry.bonus_points}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-muted-foreground flex-wrap">
                <span>{entry.quizzes_taken} quizzes</span>
                {entry.perfect_scores > 0 && (
                  <span className="text-green-600 flex items-center gap-0.5">
                    <Trophy className="w-3 h-3" />
                    {entry.perfect_scores}
                  </span>
                )}
                {showBadges && entry.badge_count > 0 && (
                  <span className="text-purple-600 flex items-center gap-0.5">
                    <Award className="w-3 h-3" />
                    {entry.badge_count}
                  </span>
                )}
              </div>
            </div>
            
            {/* Score */}
            <div className="text-right shrink-0">
              <div className={`text-lg md:text-2xl font-bold ${
                (entry.effective_score || entry.average_score) >= 90 ? "text-green-600" :
                (entry.effective_score || entry.average_score) >= 80 ? "text-blue-600" :
                (entry.effective_score || entry.average_score) >= 70 ? "text-yellow-600" : "text-foreground"
              }`}>
                {entry.effective_score !== undefined ? entry.effective_score : entry.average_score}%
              </div>
              <div className="text-[10px] md:text-xs text-muted-foreground hidden sm:block">
                {entry.bonus_points ? "Adjusted" : "Average"}
              </div>
            </div>

            {/* Adjust button (teacher only, class leaderboard only) */}
            {showAdjust && (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary shrink-0 w-8 h-8 md:w-10 md:h-10"
                onClick={() => openAdjustDialog(entry)}
                data-testid={`adjust-rank-${entry.student_id}`}
                title="Adjust Ranking"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground font-['Manrope']">Leaderboard</h1>
              <p className="text-sm text-muted-foreground">
                {isTeacher ? "Manage student rankings" : "See how you rank among your peers"}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Top 3 Podium */}
        {globalLeaderboard?.leaderboard?.length >= 3 && (
          <div className="mb-8">
            <div className="flex items-end justify-center gap-3 md:gap-6 mb-8">
              {/* 2nd Place */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-center flex flex-col items-center"
              >
                <div
                  className="rounded-full bg-gray-100 border-4 border-gray-300 flex items-center justify-center mb-2 shrink-0"
                  style={{ width: 64, height: 64, minWidth: 64, minHeight: 64, maxWidth: 64, maxHeight: 64, borderRadius: '50%' }}
                >
                  <span className="text-lg md:text-2xl font-bold text-gray-600">
                    {globalLeaderboard.leaderboard[1]?.student_name?.charAt(0)}
                  </span>
                </div>
                <div className="bg-gray-100 rounded-t-xl pt-3 pb-4 px-3 w-full" style={{ minWidth: 100 }}>
                  <Medal className="w-5 h-5 md:w-6 md:h-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs md:text-sm font-medium text-foreground truncate mx-auto">
                    {globalLeaderboard.leaderboard[1]?.student_name?.split(' ')[0]}
                  </p>
                  <p className="text-base md:text-lg font-bold text-gray-600 mt-1">
                    {globalLeaderboard.leaderboard[1]?.average_score}%
                  </p>
                </div>
              </motion.div>

              {/* 1st Place */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0 }}
                className="text-center flex flex-col items-center -mt-4"
              >
                <Crown className="w-6 h-6 md:w-8 md:h-8 text-yellow-500 mx-auto mb-2" />
                <div
                  className="rounded-full bg-yellow-100 border-4 border-yellow-400 flex items-center justify-center mb-2 shrink-0"
                  style={{ width: 80, height: 80, minWidth: 80, minHeight: 80, maxWidth: 80, maxHeight: 80, borderRadius: '50%' }}
                >
                  <span className="text-xl md:text-3xl font-bold text-yellow-600">
                    {globalLeaderboard.leaderboard[0]?.student_name?.charAt(0)}
                  </span>
                </div>
                <div className="bg-yellow-100 rounded-t-xl pt-3 pb-4 px-3 w-full" style={{ minWidth: 110 }}>
                  <Trophy className="w-6 h-6 md:w-8 md:h-8 text-yellow-500 mx-auto mb-1" />
                  <p className="text-xs md:text-sm font-medium text-foreground truncate mx-auto">
                    {globalLeaderboard.leaderboard[0]?.student_name?.split(' ')[0]}
                  </p>
                  <p className="text-lg md:text-xl font-bold text-yellow-600 mt-1">
                    {globalLeaderboard.leaderboard[0]?.average_score}%
                  </p>
                </div>
              </motion.div>

              {/* 3rd Place */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center flex flex-col items-center"
              >
                <div
                  className="rounded-full bg-amber-100 border-4 border-amber-400 flex items-center justify-center mb-2 shrink-0"
                  style={{ width: 64, height: 64, minWidth: 64, minHeight: 64, maxWidth: 64, maxHeight: 64, borderRadius: '50%' }}
                >
                  <span className="text-lg md:text-2xl font-bold text-amber-600">
                    {globalLeaderboard.leaderboard[2]?.student_name?.charAt(0)}
                  </span>
                </div>
                <div className="bg-amber-100 rounded-t-xl pt-3 pb-4 px-3 w-full" style={{ minWidth: 100 }}>
                  <Medal className="w-5 h-5 md:w-6 md:h-6 text-amber-600 mx-auto mb-1" />
                  <p className="text-xs md:text-sm font-medium text-foreground truncate mx-auto">
                    {globalLeaderboard.leaderboard[2]?.student_name?.split(' ')[0]}
                  </p>
                  <p className="text-base md:text-lg font-bold text-amber-600 mt-1">
                    {globalLeaderboard.leaderboard[2]?.average_score}%
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        <Tabs defaultValue={classId ? "class" : "global"} className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-lg">
            <TabsTrigger value="global" className="rounded-md" data-testid="tab-global">
              <TrendingUp className="w-4 h-4 mr-2" />
              Global Rankings
            </TabsTrigger>
            {classId && classLeaderboard && (
              <TabsTrigger value="class" className="rounded-md" data-testid="tab-class">
                <Users className="w-4 h-4 mr-2" />
                {classLeaderboard.class_name}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="global">
            <Card className="border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Manrope']">
                      {activeSubject ? `${activeSubject} Rankings` : "Global Leaderboard"}
                    </CardTitle>
                    <CardDescription>
                      {activeSubject ? `Top performers in ${activeSubject}` : "Top performers across all your classes"}
                    </CardDescription>
                  </div>
                  {isTeacher && globalLeaderboard?.leaderboard?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => openResetDialog("global")}
                      data-testid="reset-global-leaderboard-btn"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset Rankings
                    </Button>
                  )}
                </div>
                {/* Subject Filter Tabs */}
                {subjects.length > 1 && (
                  <div className="flex flex-wrap gap-2 pt-3" data-testid="subject-filters">
                    {isTeacher && (
                      <button
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                          !activeSubject ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={() => { setActiveSubject(null); fetchGlobalLeaderboard(null); }}
                        data-testid="subject-filter-all"
                      >
                        All Subjects
                      </button>
                    )}
                    {subjects.map((s) => (
                      <button
                        key={s}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                          activeSubject === s ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={() => setActiveSubject(s)}
                        data-testid={`subject-filter-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {subjects.length === 1 && (
                  <Badge variant="outline" className="w-fit mt-2 text-primary border-primary/30">
                    {subjects[0]}
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                {globalLeaderboard?.leaderboard?.length === 0 ? (
                  <div className="text-center py-12">
                    <Trophy className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No rankings yet</p>
                    <p className="text-sm text-muted-foreground">Take some quizzes to appear on the leaderboard!</p>
                  </div>
                ) : (
                  <LeaderboardList data={globalLeaderboard?.leaderboard || []} showBadges />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {classId && classLeaderboard && (
            <TabsContent value="class">
              <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-['Manrope']">{classLeaderboard.class_name}</CardTitle>
                    <CardDescription>{classLeaderboard.subject} class rankings</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {isTeacher && classLeaderboard?.leaderboard?.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => openResetDialog("class")}
                        data-testid="reset-class-leaderboard-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    )}
                    {isTeacher && (
                      <Badge variant="outline" className="text-blue-600 border-blue-300">
                        <SlidersHorizontal className="w-3 h-3 mr-1" />
                        Click slider to adjust
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {classLeaderboard?.leaderboard?.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                      <p className="text-muted-foreground">No rankings yet</p>
                    </div>
                  ) : (
                    <LeaderboardList data={classLeaderboard?.leaderboard || []} showAdjust={isTeacher} />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Adjust Ranking Dialog */}
        <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-primary" />
                Adjust Ranking
              </DialogTitle>
              <DialogDescription>
                Add bonus or penalty points for <strong>{adjustStudent?.student_name}</strong>. 
                Current average: {adjustStudent?.average_score}%
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Bonus / Penalty Points</Label>
                <Input
                  data-testid="bonus-points-input"
                  type="number"
                  step="0.5"
                  placeholder="e.g., +5 or -3"
                  value={adjustData.bonus_points}
                  onChange={(e) => setAdjustData({ ...adjustData, bonus_points: parseFloat(e.target.value) || 0 })}
                />
                <p className="text-xs text-muted-foreground">
                  Positive = boost ranking, Negative = lower ranking. Effective score: {Math.min(((adjustStudent?.average_score || 0) + (adjustData.bonus_points || 0)), 100).toFixed(1)}%
                </p>
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Textarea
                  data-testid="adjustment-note-input"
                  placeholder="e.g., Extra credit for project work"
                  value={adjustData.note}
                  onChange={(e) => setAdjustData({ ...adjustData, note: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => {
                    setAdjustData({ bonus_points: 0, note: adjustStudent?.adjustment_note || "" });
                  }}
                  data-testid="reset-adjustment-btn"
                >
                  Reset to 0
                </Button>
                <Button
                  data-testid="save-adjustment-btn"
                  className="flex-1 rounded-full bg-primary text-primary-foreground"
                  onClick={handleAdjustRanking}
                  disabled={adjusting}
                >
                  {adjusting ? "Saving..." : "Save Adjustment"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Reset Leaderboard Confirmation Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2 text-destructive">
                <RotateCcw className="w-5 h-5" />
                Reset {resetTarget === "class" ? "Class" : (activeSubject || "Global")} Leaderboard
              </DialogTitle>
              <DialogDescription>
                This will permanently clear all quiz scores and ranking adjustments for{" "}
                {resetTarget === "class"
                  ? `the class "${classLeaderboard?.class_name}"`
                  : activeSubject
                    ? `all classes under "${activeSubject}"`
                    : "all your classes"
                }. Students will start fresh with zero scores. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => setResetDialogOpen(false)}
                data-testid="cancel-reset-btn"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1 rounded-full"
                onClick={handleResetLeaderboard}
                disabled={resetting}
                data-testid="confirm-reset-btn"
              >
                {resetting ? "Resetting..." : "Yes, Reset Rankings"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
